<template>
  <div>
    <div class="info" v-if="!edit && !mover && !typer">
      <controls
        v-if="showControls()"
        :question="question"
        @edit="showEdit"
        @remove="remove"
        @mover="showMove"
        @typer="showTyper"
      ></controls>

      <h4 class="question-title">
        <span v-if="question.required">*</span>
        {{ question.title }}
        <small class="has-text-grey-light">[{{ question.weight }}]</small>
      </h4>

      <div
        class="question-body"
        v-for="field in question.fields"
        v-bind:key="field.choice"
      >
        <div class="question-field">{{ field.choice }}</div>
      </div>
    </div>

    <div class="edit" v-if="edit && !mover && !typer">
      <form @submit.prevent="onSubmit">
        <div class="field">
          <label class="label">Title</label>
          <div class="control has-icons-right mt-2">
            <input
              type="text"
              :class="['input', { 'is-danger': errors['title'] }]"
              v-model="data['title']"
              name="title"
            />
            <span class="icon is-small is-right" v-if="errors['title']">
              <i class="fa fa-warning"></i>
            </span>
          </div>
          <p v-if="errors['title']" class="help is-danger">
            {{ errors["title"] }}
          </p>
        </div>

        <div class="field">
          <label class="label">Weight</label>
          <div class="control has-icons-right mt-2 select is-fullwidth">
            <select name="weight" v-model="data['weight']">
              <option
                v-for="weight in weights"
                :value="weight.value"
                v-bind:key="weight.value"
                >[{{ weight.value }}] {{ weight.label }}</option
              >
            </select>
            <span class="icon is-small is-right" v-if="errors['weight']">
              <i class="fa fa-warning"></i>
            </span>
          </div>
          <p v-if="errors['weight']" class="help is-danger">
            {{ errors["weight"] }}
          </p>
        </div>

        <div class="field">
          <label class="label">Required</label>
          <div class="control has-icons-right mt-2 select is-fullwidth">
            <!-- <select name="required" disabled>
              <option>Yes</option>
            </select>-->
            <select
              name="required"
              v-model="data['required']"
              :disabled="question.interviewMandatory"
            >
              <option
                v-for="required in yesNo"
                :value="required.value"
                v-bind:key="required.value"
                >{{ required.label }}</option
              >
            </select>
            <span class="icon is-small is-right" v-if="errors['title']">
              <i class="fa fa-warning"></i>
            </span>
          </div>
          <p v-if="errors['title']" class="help is-danger">
            {{ errors["title"] }}
          </p>
        </div>

        <div class="field">
          <label class="label">Autologin</label>
          <div class="control has-icons-right mt-2 select is-fullwidth">
            <p>{{ $log(data) }}</p>
            <select name="autologin" v-model="data['autologin']">
              <option
                v-for="option in yesNo"
                :value="option.value"
                v-bind:key="option.value"
                >{{ option.label }}</option
              >
            </select>
            <span class="icon is-small is-right" v-if="errors['autologin']">
              <i class="fa fa-warning"></i>
            </span>
          </div>
          <p v-if="errors['autologin']" class="help is-danger">
            {{ errors["autologin"] }}
          </p>
          <p class="help">
            This option tells Virtriage to automatically login a user into
            Visuwell or not before they are redirected to the waiting room.
          </p>
        </div>

        <div
          class="field"
          v-for="(field, index) in data.fields"
          v-bind:key="index"
        >
          <label class="label">Item {{ index + 1 }}</label>
          <div class="columns">
            <div class="column">
              <div class="control field-options">
                <input
                  type="text"
                  :class="['input', { 'is-danger': errors[`choice${index}`] }]"
                  v-model="field['choice']"
                  placeholder="Label"
                  name="title"
                />
                <span
                  class="icon is-small is-right"
                  v-if="errors[`choice${index}`]"
                >
                  <i class="fa fa-warning"></i>
                </span>
              </div>
              <p v-if="errors[`choice${index}`]" class="help is-danger">
                {{ errors[`choice${index}`] }}
              </p>
            </div>
            <div class="column is-3 field-options">
              <div class="select">
                <select v-model="field['weight']">
                  <option
                    v-for="weight in fieldWeights"
                    :value="weight.value"
                    :key="weight.value"
                    >[{{ weight.value }}] {{ weight.label }}</option
                  >
                </select>
              </div>
            </div>
            <div class="column is-4 field-options">
              <div class="select">
                <select v-model="field['skipTo']">
                  <option value>Skip to...</option>
                  <option
                    v-for="page in pages"
                    :value="page.id"
                    v-bind:key="page.id"
                    >{{ page.attributes.title }}</option
                  >
                  <option disabled value>--Alternate Endings:--</option>
                  <option
                    v-for="alternateEnding in alternateEndings"
                    :value="alternateEnding.id"
                    :key="alternateEnding.id"
                    >{{ alternateEnding.alternateEndingHeader }}</option
                  >
                  <option disabled value>--Default Ending:--</option>
                  <option value="summary">Review and Submit</option>
                </select>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column is-4 field-options">
              <div class="select">
                <select v-model="field['image']">
                  <option value>Select an image</option>
                  <option
                    v-for="image in images"
                    :value="image"
                    v-bind:key="image.src"
                    >{{ image.title }}</option
                  >
                </select>
              </div>
            </div>

            <div class="column is-4 field-options">
              <div class="control field-options">
                <input
                  placeholder="Location ID"
                  type="text"
                  :class="[
                    'input',
                    { 'is-danger': errors[`organizationLocationId${index}`] }
                  ]"
                  v-model="field['organizationLocationId']"
                  name="organizationLocationId"
                />
                <span
                  class="icon is-small is-right"
                  v-if="errors[`organizationLocationId${index}`]"
                >
                  <i class="fa fa-warning"></i>
                </span>
              </div>
              <p
                v-if="errors[`organizationLocationId${index}`]"
                class="help is-danger"
              >
                {{ errors[`organizationLocationId${index}`] }}
              </p>
            </div>

            <div class="column is-1 field-options">
              <i
                class="fa fa-plus-square-o mr1 pointer"
                @click="addField(index)"
              ></i>
              <i
                class="fa fa-minus-square-o pointer"
                v-if="index > 1"
                @click="removeField(index)"
              ></i>
              <i
                class="fa fa-minus-square-o has-text-grey-light"
                v-if="index <= 1"
              ></i>
            </div>
          </div>

          <div style="width: 200px;" v-if="field['image']">
            <img :src="field['image'].src" :alt="field['image'].title" />
          </div>
        </div>

        <div class="field is-grouped mt2 has-text-right">
          <div class="control">
            <button
              :class="['button', 'is-primary', { 'is-loading': loading }]"
            >
              Update
            </button>
            <button type="button" class="button is-link" @click="hideEdit()">
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>

    <question-move
      v-if="mover"
      :question="question"
      @hide="hideMove"
    ></question-move>
    <question-type
      v-if="typer"
      :question="question"
      @hide="hideTyper"
    ></question-type>
  </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import baseQuestion from "./baseQuestion";
import RoutingRules from "../routing_rules/RoutingRules";
import CasegridService from "@/services/CasegridService";

export default {
  name: "casegrid",

  mixins: [baseQuestion],

  computed: {
    pages() {
      const pages = this.$store.state.interview.pages;
      return _.filter(pages, o => o.id !== this.question.interviewPageId);
    },
    alternateEndings() {
      const alternateEndings = this.$store.state.interview.alternateEndings;
      return alternateEndings;
    }
  },

  mounted() {
    CasegridService.images().then(images => {
      this.images = images;
    });
  },

  methods: {
    showControls() {
      // do not allow any changes to phq-9 specific questions
      return this.canEdit && this.question.dataType !== "dt_phq9";
    },
    addField(index) {
      this.data.fields.splice(index + 1, 0, { choice: "", weight: 3 });
    },
    removeField(index) {
      this.data.fields.splice(index, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
.field-options {
  align-self: center;
  justify-content: center;
}
</style>
